import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { API } from "@/lib/site";

const STANDARDS = [
  "Class 1 - 5 (Primary)",
  "Class 6 - 8 (Middle School)",
  "Class 9 - 10 (Secondary Boards)",
  "Class 11 - 12 (Science / Commerce / Arts)",
  "Competitive Exam Foundation",
  "College / Undergrad Support",
];
const BOARDS = ["CBSE", "ICSE / ISC", "State Board", "Cambridge IGCSE / A-Levels", "IB (International Baccalaureate)", "Other"];
const MODES = [
  "Home Tuitions (Tutor visits student)",
  "Personal 1-on-1 Online (Remote)",
  "Small Group Batch (Online/Offline)",
];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-navy focus:ring-2 focus:ring-navy/15";
const labelCls =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500";

export default function InquiryForm() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "", standard: "", board: "", tuition_mode: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.location || !form.standard || !form.board || !form.tuition_mode) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiry`, form);
      toast.success("Inquiry sent! We'll call you within 24 hours.");
      setDone(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail === undefined
          ? "Could not send. Please call us at +91 63782 31794."
          : String(err.response.data.detail)
      );
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="inquiry-success-card"
        className="flex h-full flex-col items-center justify-center rounded-3xl border border-teal-200 bg-[#F0FDFA] p-10 text-center"
      >
        <CheckCircle2 className="h-14 w-14 text-teal-600" />
        <h3 className="mt-5 font-serif text-2xl font-bold text-slate-900">
          Inquiry received!
        </h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
          Thank you, {form.name.split(" ")[0]}. Our team will call you on{" "}
          <span className="font-semibold">{form.phone}</span> within 24 hours to
          schedule your 2 free demo lectures.
        </p>
        <button
          data-testid="inquiry-another-btn"
          onClick={() => {
            setDone(false);
            setForm({ name: "", phone: "", email: "", location: "", standard: "", board: "", tuition_mode: "", message: "" });
          }}
          className="mt-6 rounded-full border border-navy/20 px-5 py-2 text-sm font-semibold text-navy transition hover:bg-navy hover:text-white"
        >
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="inq-name">Student / Parent Name *</label>
          <input id="inq-name" data-testid="inquiry-name-input" className={inputCls} placeholder="e.g., Rajesh Sharma / Aarav" value={form.name} onChange={set("name")} required maxLength={120} />
        </div>
        <div>
          <label className={labelCls} htmlFor="inq-phone">Contact Number *</label>
          <input id="inq-phone" data-testid="inquiry-phone-input" type="tel" className={inputCls} placeholder="+91 98765 43210" value={form.phone} onChange={set("phone")} required maxLength={20} />
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="inq-email">Email Address *</label>
        <input id="inq-email" data-testid="inquiry-email-input" type="email" className={inputCls} placeholder="name@example.com" value={form.email} onChange={set("email")} required maxLength={200} />
      </div>
      <div>
        <label className={labelCls} htmlFor="inq-location">Current Location *</label>
        <input id="inq-location" data-testid="inquiry-location-input" className={inputCls} placeholder="e.g., Jaipur, Rajasthan" value={form.location} onChange={set("location")} required maxLength={120} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="inq-standard">Student's Standard / Class *</label>
          <select id="inq-standard" data-testid="inquiry-standard-select" className={inputCls} value={form.standard} onChange={set("standard")} required>
            <option value="" disabled>Select class</option>
            {STANDARDS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="inq-board">Educational Board *</label>
          <select id="inq-board" data-testid="inquiry-board-select" className={inputCls} value={form.board} onChange={set("board")} required>
            <option value="" disabled>Select board</option>
            {BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="inq-mode">Preferred Tuition Mode *</label>
        <select id="inq-mode" data-testid="inquiry-mode-select" className={inputCls} value={form.tuition_mode} onChange={set("tuition_mode")} required>
          <option value="" disabled>Select mode</option>
          {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor="inq-msg">Specific Subjects & Learning Goals</label>
        <textarea id="inq-msg" data-testid="inquiry-message-input" className={`${inputCls} min-h-[90px] resize-y`} placeholder="e.g., Need focused math and physics help for Class 10 CBSE; evening batch after 6 PM." value={form.message} onChange={set("message")} maxLength={2000} />
      </div>
      <button
        type="submit"
        data-testid="inquiry-submit-btn"
        disabled={sending}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-navy-dark disabled:cursor-not-allowed disabled:opacity-60"
      >
        {sending ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
        ) : (
          <>
            <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            Send Inquiry — Book 2 Free Demos
          </>
        )}
      </button>
      <p className="text-center text-xs text-slate-400">
        Your details reach our team directly. No spam, ever.
      </p>
    </form>
  );
}
